import AllCategoriesList from 'components/AllCategoriesList/AllCategoriesList'
import Layout from 'components/Layout/Layout'
import OgMetadata from 'components/OgMetadata/OgMetadata'
import { DataLayerPageType } from 'frontend/enums'
import { MINORITY_LANGUAGES } from 'frontend/lib/constants'
import { CategoryItem } from 'frontend/types'
import usePageTracking from 'frontend/utils/hooks/usePageTracking'
import { getOrigin } from 'frontend/utils/urlHelpers'
import { GetServerSideProps } from 'next'
import Head from 'next/head'
import {
  getAllAdaptedCategories,
  getMinorityLanguageCategories,
} from 'utils/categoryUtil'
import { sortByNameIgnoreCase } from 'utils/sort'

interface Props {
  categoriesForMenu: CategoryItem[]
  origin: string
  allCategories: CategoryItem[]
  minorityLanguageCategories: CategoryItem[]
  schoolCategories: CategoryItem[]
  childCategories: CategoryItem[]
}

export const AllCategoriesPage = ({
  categoriesForMenu,
  origin,
  allCategories,
  minorityLanguageCategories,
  schoolCategories,
  childCategories,
}: Props) => {
  usePageTracking(DataLayerPageType.ALL_PROGRAMS_PAGE)

  return (
    <>
      <Head>
        <title>Alla kategorier - UR Play</title>
        <meta name="description" content="En översikt över alla kategorier" />
        <link rel="canonical" href={`${origin}/bladdra/alla-kategorier`} />
      </Head>
      <OgMetadata
        title="Alla program på UR Play"
        description="En översikt över alla kategorier"
        type="article"
        sharePath={'/bladdra/alla-program'}
      />
      <Layout
        categories={categoriesForMenu}
        minorityLanguageCategories={minorityLanguageCategories}
      >
        <AllCategoriesList
          allCategories={allCategories}
          minorityLanguageCategories={minorityLanguageCategories}
          schoolCategories={schoolCategories}
          childCategories={childCategories}
        />
      </Layout>
    </>
  )
}

export const getServerSideProps: GetServerSideProps = async ctx => {
  const categories = await getAllAdaptedCategories()
  const origin = getOrigin(ctx)

  const allCategories = categories
    .filter(
      category =>
        !MINORITY_LANGUAGES.includes(category.slug) &&
        !category.isSchoolCategory &&
        !category.isChildCategory,
    )
    .sort((categoryA, categoryB) =>
      sortByNameIgnoreCase(categoryA.name, categoryB.name),
    )

  const minorityLanguageCategories = getMinorityLanguageCategories(categories)

  const schoolCategories = categories
    .filter(
      category =>
        category.isSchoolCategory &&
        !MINORITY_LANGUAGES.includes(category.slug),
    )
    .sort((categoryA, categoryB) =>
      sortByNameIgnoreCase(categoryA.name, categoryB.name),
    )

  const childCategories = categories
    .filter(
      category =>
        category.isChildCategory && !MINORITY_LANGUAGES.includes(category.slug),
    )
    .sort((categoryA, categoryB) =>
      sortByNameIgnoreCase(categoryA.name, categoryB.name),
    )

  const categoriesForMenu = categories.filter(
    category => category.prioritized && !category.hideCategory,
  )

  return {
    props: {
      categoriesForMenu,
      allCategories,
      minorityLanguageCategories,
      childCategories,
      schoolCategories,
      origin,
    },
  }
}

export default AllCategoriesPage
